import { mutateStockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { LotSelectComplete } from "@/features/ui/autoComplete/lot/lot-\bselectComplete";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  StockPlansGet200ResponseRowsInner,
  WmsApiStockPlansStockPlanIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface StockPlanUpdateFormProps {
  formatterProps: StockPlansGet200ResponseRowsInner;
}

export const StockPlanUpdateForm = (params: StockPlanUpdateFormProps) => {
  const { formatterProps } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate: updateMutate } = useMutation(
    (params: WmsApiStockPlansStockPlanIdPutRequest) =>
      mutateStockPlans
        .update(params)
        .mutationFn(params as WmsApiStockPlansStockPlanIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["stockPlans"]);
        closeModal(form.values);
        customAlert(
          t("message.editSuccess", { item: t("stockPlan.stockPlan") }),
          t("common.editSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
          t("common.editFail", { item: t("stockPlan.stockPlan") }),
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      partnerCode: formatterProps.partnerCode,
      scheduledAt: formatterProps.scheduledAt ? new Date(formatterProps.scheduledAt) : undefined,
      itemCode: formatterProps.itemCode,
      lotId: formatterProps.lotId,
      quantity: formatterProps.quantity,
      userCode: formatterProps.userCode,
      sourceLocationCode: formatterProps.sourceLocationCode,
      targetLocationCode: formatterProps.targetLocationCode,
      transportationId: formatterProps.transportationId,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        stockPlanId: formatterProps.id!,
        stockPlansGetRequest: {
          scheduledAt: form.values.scheduledAt?.toDateString()!,
          itemCode: form.values.itemCode!,
          direction: formatterProps.direction!,
          partnerCode: form.values.partnerCode!,
          lotId: form.values.lotId!,
          quantity: form.values.quantity!,
          userCode: form.values.userCode!,
          sourceLocationCode: form.values.sourceLocationCode!,
          targetLocationCode: form.values.targetLocationCode!,
          transportationId: form.values.transportationId!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
        t("common.editFail", { item: t("stockPlan.stockPlan") }),
        "red",
      );
    }
  };

  return (
    <>
      <Update.Wrapper>
        <PartnerAutoComplete
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          {...form.getInputProps("partnerCode")}
          defaultValue={formatterProps.partnerCode}
          error={form.isDirty("partnerCode") ? t("common.toSave") : ""}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("stockPlan.scheduledAt")}
          withAsterisk
          valueFormat="YYYY-MM-DD"
          {...form.getInputProps("scheduledAt")}
          error={form.isDirty("scheduledAt") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("scheduledAt") ? "red !important" : "",
              borderColor: form.isDirty("scheduledAt") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <ItemAutoComplete
          label={t("item.code")}
          placeholder={t("placeholder.select", { item: t("item.code") })}
          withAsterisk
          {...form.getInputProps("itemCode")}
          defaultValue={formatterProps.itemCode}
          error={form.isDirty("itemCode") ? t("common.toSave") : ""}
        />
        <LotSelectComplete
          label={t("lot.lot")}
          placeholder={t("placeholder.select", { item: t("lot.lot") })}
          withAsterisk
          itemCodeValue={form.values.itemCode ?? ""}
          {...form.getInputProps("lotId")}
          error={form.isDirty("lotId") ? t("common.toSave") : ""}
          defaultValue={formatterProps.lotId?.toString()}
          newIdValue={formatterProps.lotId}
        />
        <Update.Input
          label={t("inventory.quantity")}
          placeholder={t("placeholder.input", { item: t("inventory.quantity") })}
          withAsterisk
          {...form.getInputProps("quantity")}
          defaultValue={formatterProps.quantity}
          error={form.isDirty("quantity") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("quantity") ? "red !important" : "",
              borderColor: form.isDirty("quantity") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <UserAutoComplete
          label={t("order.manager")}
          placeholder={t("placeholder.select", { item: t("order.manager") })}
          {...form.getInputProps("userCode")}
          defaultValue={formatterProps.userCode}
          error={form.isDirty("userCode") ? t("common.toSave") : ""}
        />
        <LocationAutoComplete
          label={t("inventory.fromLocation")}
          placeholder={t("placeholder.select", { item: t("inventory.fromLocation") })}
          {...form.getInputProps("sourceLocationCode")}
          defaultValue={formatterProps.sourceLocationCode}
          error={form.isDirty("sourceLocationCode") ? t("common.toSave") : ""}
        />
        <LocationAutoComplete
          label={t("inventory.toLocation")}
          placeholder={t("placeholder.select", { item: t("inventory.toLocation") })}
          {...form.getInputProps("targetLocationCode")}
          defaultValue={formatterProps.targetLocationCode}
          error={form.isDirty("targetLocationCode") ? t("common.toSave") : ""}
        />
        <Update.ButtonBox>
          <Update.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Update.Button>
          <Update.Button
            disabled={validateFormValues({
              pageType: "stockPlan",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {t("common.edit", { item: t("stockPlan.stockPlan") })}
          </Update.Button>
        </Update.ButtonBox>
      </Update.Wrapper>
    </>
  );
};
